import {
  isCancelled,
  isHistory,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import type { CoachingSession } from '@efdigital/ll-addon-data';
import type { CoachingPartnership } from '@efdigital/ll-addon-data/types/coaching/coaching-partnership';
import { queryCoachingSessions } from '@efdigital/ll-admin-v2/data/builders/coaching/coaching-sessions';
import { getPaginationTotal } from '@efdigital/ll-admin-v2/helpers/get-pagination-total';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Request } from '@warp-drive/ember';

/**
 * Enough to summarise: a partnership's sessions are bounded by the plan's quota,
 * so this window covers every realistic case and the true count still comes from
 * the response meta.
 */
const SUMMARY_LIMIT = 100;

interface CoachingPartnershipSessionsSignature {
  Args: {
    partnership: CoachingPartnership;
  };
  Element: HTMLDivElement;
}

/** Counts by outcome, using the addon's own status vocabulary. */
function summarise(sessions: CoachingSession[] | undefined) {
  const all = sessions ?? [];
  const cancelled = all.filter((s) => isCancelled(s.status)).length;
  const past = all.filter(
    (s) => isHistory(s.status) && !isCancelled(s.status)
  ).length;
  return {
    cancelled,
    past,
    upcoming: all.length - cancelled - past,
  };
}

/**
 * Read-only per-partnership session overview.
 *
 * Deliberately a summary rather than a table: this sits inside an expanded row
 * of an already-paginated list, where a six-column table has nowhere to go. The
 * detail lives on the sessions page, which has the room for it and the filters
 * to go with it — so this counts the outcomes and links there.
 *
 * Loads lazily, so an unopened partnership costs nothing.
 */
export class CoachingPartnershipSessions extends Component<CoachingPartnershipSessionsSignature> {
  @tracked expanded = false;

  toggle = () => {
    this.expanded = !this.expanded;
  };

  get query() {
    return queryCoachingSessions(
      { partnershipId: this.args.partnership.id! },
      { offset: 0, limit: SUMMARY_LIMIT }
    );
  }

  /**
   * A partnership's coach and coachee never change, so linking with both ids
   * scopes the sessions page the same way partnershipId would — but leaves
   * the page's own coach/coachee pickers correctly pre-filled, which a bare
   * partnershipId can't since they only read coachUserId/coacheeUserId.
   */
  get sessionsQuery() {
    return {
      coachUserId: this.args.partnership.coachUserId,
      coacheeUserId: this.args.partnership.user,
    };
  }

  <template>
    <div ...attributes>
      <button
        type="button"
        class="btn btn-sm btn-link p-0 text-decoration-none"
        aria-expanded="{{if this.expanded 'true' 'false'}}"
        {{on "click" this.toggle}}
      >
        {{if this.expanded "Hide sessions" "Sessions"}}
      </button>

      {{#if this.expanded}}
        <Request @query={{this.query}}>
          <:content as |content|>
            {{#if content.data.length}}
              {{#let (summarise content.data) as |counts|}}
                <p
                  class="d-flex flex-wrap align-items-center gap-2 small mb-0 mt-2"
                >
                  <span class="fw-semibold">{{getPaginationTotal content}}
                    sessions</span>
                  {{#if counts.past}}
                    <span class="badge text-bg-success">{{counts.past}}
                      completed</span>
                  {{/if}}
                  {{#if counts.upcoming}}
                    <span class="badge text-bg-info">{{counts.upcoming}}
                      upcoming</span>
                  {{/if}}
                  {{#if counts.cancelled}}
                    <span class="badge text-bg-secondary">{{counts.cancelled}}
                      cancelled</span>
                  {{/if}}
                  <LinkTo
                    @route="coaching.sessions"
                    @query={{this.sessionsQuery}}
                  >View all &rarr;</LinkTo>
                </p>
              {{/let}}
            {{else}}
              <p class="text-muted small mb-0 mt-2">No sessions yet.</p>
            {{/if}}
          </:content>
          <:loading>
            <p class="text-muted small mb-0 mt-2">Loading…</p>
          </:loading>
          <:error>
            <p class="text-danger small mb-0 mt-2">Couldn’t load sessions.</p>
          </:error>
        </Request>
      {{/if}}
    </div>
  </template>
}
